<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * RED-386: "Mis läheb mootorile" (design-spec §2). Shows what the engine would
 * send for the unsaved draft: the three layers (Üldjuhis with the items filled
 * in, the report, one question), the model strip, the send order in rounds and
 * the questions that are not sent, with the reason. Every value comes from the
 * preview endpoint; nothing here is computed from the draft.
 */
const props = defineProps({
  preview: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  failed: { type: Boolean, default: false },
})

const { t, te } = useI18n()

const PREVIEW_LINES = 4

// RED-361: the Estonian level label ("Madal"), as on Mootori seaded; an unknown API value stays as it is.
const thinkingLabel = computed(() => {
  const level = props.preview?.strip?.thinkingLevel
  const key = `messages.admin.report_qc.settings.thinking.${level}`

  return te(key) ? t(key) : level
})

const showWholeFrame = ref(false)
const openKey = ref(null)

const usd = value => new Intl.NumberFormat('et-EE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
const number = value => new Intl.NumberFormat('et-EE').format(value)

const questionsByKey = computed(() => Object.fromEntries((props.preview?.questions ?? []).map(question => [question.key, question])))

const frameText = computed(() => (props.preview?.systemPrompt ?? '').split('\n').slice(0, PREVIEW_LINES).join('\n'))

// RED-361: the whole frame in three parts, so the item texts (the checklist slot) can be marked.
const frameParts = computed(() => {
  const text = props.preview?.systemPrompt ?? ''
  const slot = props.preview?.checklistSlot

  if (!slot || slot.end <= slot.start) {
    return { before: text, slot: '', after: '' }
  }

  return { before: text.slice(0, slot.start), slot: text.slice(slot.start, slot.end), after: text.slice(slot.end) }
})

// Layer 3 shows the question that goes first (or the one opened below).
const sampleQuestion = computed(() => questionsByKey.value[openKey.value ?? props.preview?.rounds?.[0]?.[0]] ?? null)

const toggle = key => {
  openKey.value = openKey.value === key ? null : key
}
</script>

<template>
  <Card class="h-full" data-testid="checklist-preview">
    <template #title>
      <div class="flex items-center justify-between gap-2">
        <span>{{ t('messages.admin.report_qc.preview.title') }}</span>
        <i v-if="loading" class="pi pi-spin pi-spinner text-sm text-gray-400" :aria-label="t('messages.admin.report_qc.preview.loading')" />
      </div>
    </template>
    <template #content>
      <Message v-if="failed" severity="warn" :closable="false" class="mb-3" data-testid="checklist-preview-error">
        {{ t('messages.admin.report_qc.preview.error') }}
      </Message>

      <div v-if="!preview" class="flex flex-col gap-3">
        <Skeleton v-for="n in 4" :key="n" height="3rem" />
      </div>

      <div v-else class="flex flex-col gap-4 text-sm">
        <p class="text-gray-600">{{ t('messages.admin.report_qc.preview.intro') }}</p>

        <section>
          <h3 class="mb-1 font-medium">{{ t('messages.admin.report_qc.preview.layer_frame') }}</h3>
          <pre v-if="showWholeFrame" class="max-h-[32rem] overflow-auto rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs whitespace-pre-wrap text-gray-700" data-testid="checklist-preview-frame">{{ frameParts.before }}<template v-if="frameParts.slot"><span class="my-1 block rounded bg-blue-100 px-2 py-0.5 font-sans text-blue-800" data-testid="checklist-preview-slot-hint">{{ t('messages.admin.report_qc.preview.slot_hint') }}</span><span class="bg-blue-50" data-testid="checklist-preview-slot">{{ frameParts.slot }}</span></template>{{ frameParts.after }}</pre>
          <pre v-else class="max-h-[32rem] overflow-auto rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs whitespace-pre-wrap text-gray-700" data-testid="checklist-preview-frame">{{ frameText }}</pre>
          <Button
            :label="t(showWholeFrame ? 'messages.admin.report_qc.preview.show_less' : 'messages.admin.report_qc.preview.show_all')"
            link
            size="small"
            class="px-0!"
            data-testid="checklist-preview-frame-toggle"
            @click="showWholeFrame = !showWholeFrame"
          />
        </section>

        <section>
          <h3 class="mb-1 font-medium">{{ t('messages.admin.report_qc.preview.layer_report') }}</h3>
          <pre class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs whitespace-pre-wrap text-gray-700">{{ preview.reportLead }}
{{ t('messages.admin.report_qc.preview.report_placeholder') }}</pre>
        </section>

        <section v-if="sampleQuestion">
          <h3 class="font-medium" data-testid="checklist-preview-question-heading">{{ t('messages.admin.report_qc.preview.layer_question', { key: sampleQuestion.key }) }}</h3>
          <p class="mb-1 text-xs text-gray-500">{{ t('messages.admin.report_qc.preview.layer_question_hint') }}</p>
          <pre class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs whitespace-pre-wrap text-gray-700" data-testid="checklist-preview-question">{{ sampleQuestion.text }}<template v-if="sampleQuestion.suffix">

<span class="text-gray-500">{{ sampleQuestion.suffix }}</span></template></pre>
        </section>

        <p class="font-mono text-xs text-gray-500" data-testid="checklist-preview-strip">
          {{ t('messages.admin.report_qc.preview.strip', { model: preview.strip.model, tokens: number(preview.strip.maxOutputTokens), level: thinkingLabel }) }}
        </p>

        <section class="flex flex-col gap-3" data-testid="checklist-preview-rounds">
          <div v-for="(round, index) in preview.rounds" :key="index">
            <h4 class="mb-1 text-xs font-medium text-gray-600 uppercase">
              {{ round.length === 1 ? t('messages.admin.report_qc.preview.round_one', { n: index + 1 }) : t('messages.admin.report_qc.preview.round_many', { n: index + 1, m: round.length }) }}
            </h4>
            <ul class="flex flex-col gap-1">
              <li v-for="key in round" :key="key" class="rounded-md border border-gray-200" :data-testid="`checklist-preview-call-${key}`">
                <button type="button" class="flex w-full items-start gap-2 px-2 py-1 text-left hover:bg-gray-50" :class="{ 'bg-blue-50': sampleQuestion?.key === key }" @click="toggle(key)">
                  <span class="w-8 shrink-0 pt-0.5 font-mono text-xs text-gray-500">{{ key }}</span>
                  <span class="min-w-0 flex-1">
                    <span v-for="item in questionsByKey[key]?.items ?? []" :key="item.number" class="block"><span class="mr-1 font-mono text-xs text-gray-500">{{ item.number }}</span>{{ item.title }}</span>
                  </span>
                  <Tag v-if="questionsByKey[key]?.indexed" :value="t('messages.admin.report_qc.preview.evs_chip')" severity="info" class="text-xs" />
                </button>
                <pre v-if="openKey === key" class="border-t border-gray-200 px-2 py-1 font-mono text-xs whitespace-pre-wrap text-gray-700">{{ questionsByKey[key]?.text }}</pre>
              </li>
            </ul>
          </div>
        </section>

        <section v-if="preview.skipped.length" data-testid="checklist-preview-skipped">
          <h4 class="mb-1 text-xs font-medium text-gray-600 uppercase">{{ t('messages.admin.report_qc.preview.not_sent') }}</h4>
          <ul class="flex flex-col gap-1 opacity-55">
            <li v-for="row in preview.skipped" :key="row.key" class="flex flex-wrap items-center gap-2 rounded-md border border-gray-200 px-2 py-1" :data-testid="`checklist-preview-skipped-${row.key}`">
              <span class="w-8 shrink-0 font-mono text-xs text-gray-500">{{ row.key }}</span>
              <span class="min-w-0 flex-1">
                <span v-for="item in questionsByKey[row.key]?.items ?? []" :key="item.number" class="block"><span class="mr-1 font-mono text-xs text-gray-500">{{ item.number }}</span>{{ item.title }}</span>
              </span>
              <span class="text-xs text-gray-600">{{ t(`messages.admin.report_qc.preview.reason_${row.reason}`) }}</span>
            </li>
          </ul>
        </section>

        <footer class="flex flex-col gap-2 border-t border-gray-200 pt-3 text-xs text-gray-600">
          <p class="font-medium text-gray-800" data-testid="checklist-preview-footer">
            {{ t('messages.admin.report_qc.preview.footer', { calls: preview.calls, rounds: preview.rounds.length, min: usd(preview.typical.minUsd), max: usd(preview.typical.maxUsd) }) }}
          </p>
          <p v-if="preview.capUsd !== null">{{ t('messages.admin.report_qc.preview.c3', { cap: usd(preview.capUsd) }) }}</p>
          <p>{{ t('messages.admin.report_qc.preview.c4') }}</p>
        </footer>
      </div>
    </template>
  </Card>
</template>
